/**
 * The API's clock as seen through its responses, so callers can stamp cursors in the server's timeline instead of
 * the browser's.
 *
 * Every response carries a `Date` header stamped when the server produced it. The transport reports each one here,
 * and the difference to the local receive time is kept as an offset; `serverNow()` is the local clock plus that
 * offset. The header has one-second resolution and the offset absorbs the response's network latency, so the estimate
 * is within a couple of seconds of the server — well inside the overlap every cursor already applies. Before any
 * response has been seen the offset is zero and `serverNow()` is the local clock.
 */

let offsetMs = 0;
let observed = false;

/** Records one response's `Date` header against the moment it arrived; ignores a missing or unparsable header. */
export function observeServerDate(dateHeader: string | null, receivedAtMs: number = Date.now()): void {
  if (!dateHeader) {
    return;
  }
  const serverMs = Date.parse(dateHeader);
  if (!Number.isFinite(serverMs)) {
    return;
  }
  offsetMs = serverMs - receivedAtMs;
  observed = true;
}

/** The current instant on the server's clock, as best the responses seen so far can tell. */
export function serverNow(): Date {
  return new Date(Date.now() + offsetMs);
}

/** Whether any response has calibrated the clock yet. */
export function hasServerClock(): boolean {
  return observed;
}

/** Test-only reset of the calibration. */
export function __resetServerClockForTests(): void {
  offsetMs = 0;
  observed = false;
}
